import axios from 'axios';
import { useQuery } from 'react-query';
import { resolveSignedImageRequest } from '../../utils/mediaUrl';

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#f3f3f3" offset="20%" />
      <stop stop-color="#e0e0e0" offset="50%" />
      <stop stop-color="#f3f3f3" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#f3f3f3" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`;

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

type SignedImageProps = {
  src: string;
  alt: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  skipUrlFetching?: boolean;
  width?: string;
  height?: string;
};

const fetchImageSignedUrl = async (key: string) => {
  const resolved = resolveSignedImageRequest(key);
  if (resolved.kind === 'direct') {
    return { signedURL: resolved.src };
  }

  try {
    const { data } = await axios.get(resolved.requestUrl);
    return data as { signedURL: string };
  } catch (error) {
    console.error('SignedImage: Failed to fetch signed URL for key:', key, error);
    return { signedURL: '/assets/default-profile.webp' };
  }
};

const SignedImage = ({
  src,
  alt,
  objectFit = 'cover',
  skipUrlFetching = false,
  width = '100%',
  height = '100%',
}: SignedImageProps) => {
  // `/media?key=` is an API locator, not a static file. Always sign those even if
  // a caller passed skipUrlFetching because the path starts with `/`.
  const needsSigning = resolveSignedImageRequest(src).kind === 'sign';
  const { data, isLoading } = useQuery(['image', src], () => fetchImageSignedUrl(src), {
    enabled: needsSigning || !skipUrlFetching,
  });

  const imageSrc = data?.signedURL ?? src;

  // Always use regular img tag to completely avoid Next.js optimization
  if (isLoading) {
    return (
      <img
        src={`data:image/svg+xml;base64,${toBase64(shimmer(10, 10))}`}
        alt={alt}
        style={{
          objectFit: objectFit as any,
          width,
          height,
        }}
      />
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      style={{
        objectFit: objectFit as any,
        width,
        height,
      }}
    />
  );
};

export default SignedImage;
